import { Outlet, createFileRoute, redirect } from '@tanstack/react-router';
import { gateOnboardingIncomplete, sessionQuery } from '@/lib/use-session';

// Auth gate for the whole onboarding flow. Onboarding is reached via
// `/auth/finishing`, which only routes here once Clerk has a live session —
// but the steps call authed endpoints (`/me/context`, photo upload, places),
// so a visitor who lands here without a session (direct nav, expired session)
// would see every call 401. Mirror the `_authed` guard: resolve the session
// and bounce unauthenticated visitors to `/auth/login`.
//
// IMPORTANT: redirects must escape the try/catch — TanStack's `redirect()`
// returns a `Response`, which a naive catch swallows. Isolate the fetch and
// throw the redirect outside (same shape as `_authed.tsx`).
export const Route = createFileRoute('/onboarding')({
  beforeLoad: async ({ context, location }) => {
    const unauthRedirect = redirect({ to: '/auth/login', search: { from: location.href } });
    let ctx;
    try {
      ctx = await context.queryClient.ensureQueryData(sessionQuery);
    } catch {
      throw unauthRedirect;
    }
    if (ctx === null) {
      throw unauthRedirect;
    }
    // Already finished onboarding? Don't show the flow again — bounce to
    // /feed. Inverse of the `_authed`/`_public` gate; together they make
    // routing self-correcting, so a stale session cache or a back-button
    // can't strand a completed user on the profile step. Safe mid-tour:
    // completion is stamped only on a step's final-screen click, which
    // navigates out to /feed — this never fires between tour steps.
    gateOnboardingIncomplete(ctx);
    return { session: ctx.session, member: ctx.member };
  },
  component: OnboardingLayout,
});

// Each step renders its own `OnboardingShell`; this layout is just the
// guarded outlet.
function OnboardingLayout() {
  return <Outlet />;
}
